import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { GearNeedsApi, GearNeed, CreateGearNeedRequest, UpdateGearNeedRequest, CreateGearClaimRequest, UpdateGearClaimRequest } from '@/shared/api/gearNeeds'
import { useAuth } from '@/modules/auth'

interface UseGearNeedsReturn {
  gearNeeds: GearNeed[]
  isLoading: boolean
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  isClaiming: boolean
  error: string | null
  createGearNeed: (data: CreateGearNeedRequest) => Promise<GearNeed>
  updateGearNeed: (needId: string, data: UpdateGearNeedRequest) => Promise<GearNeed>
  deleteGearNeed: (needId: string) => Promise<void>
  claimGearNeed: (needId: string, data: CreateGearClaimRequest) => Promise<void>
  updateGearClaim: (needId: string, data: UpdateGearClaimRequest) => Promise<void>
  unclaimGearNeed: (needId: string) => Promise<void>
  refreshGearNeeds: () => Promise<void>
}

export function useGearNeeds(tripId: string): UseGearNeedsReturn {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // Create API instance with auth token
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    return localStorage.getItem('access_token')
  }, [])

  const gearNeedsApi = useMemo(() => new GearNeedsApi(getAuthToken), [getAuthToken])

  const { data: gearNeeds = [], isLoading, error } = useQuery({
    queryKey: ['gearNeeds', tripId],
    queryFn: () => gearNeedsApi.getTripGearNeeds(tripId),
    enabled: !!tripId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  // Create gear need mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateGearNeedRequest) => gearNeedsApi.createGearNeed(tripId, data),
    onSuccess: (newNeed) => {
      queryClient.setQueryData<GearNeed[]>(['gearNeeds', tripId], (old = []) => [...old, newNeed])
    }
  })

  // Update gear need mutation
  const updateMutation = useMutation({
    mutationFn: ({ needId, data }: { needId: string; data: UpdateGearNeedRequest }) => 
      gearNeedsApi.updateGearNeed(needId, data),
    onSuccess: (updatedNeed, { needId }) => {
      queryClient.setQueryData<GearNeed[]>(['gearNeeds', tripId], (old = []) => 
        old.map(need => need.id === needId ? updatedNeed : need)
      )
    }
  })

  // Delete gear need mutation
  const deleteMutation = useMutation({
    mutationFn: (needId: string) => gearNeedsApi.deleteGearNeed(needId),
    onSuccess: (_, needId) => {
      queryClient.setQueryData<GearNeed[]>(['gearNeeds', tripId], (old = []) => 
        old.filter(need => need.id !== needId)
      )
    }
  })

  // Claim gear need mutation
  const claimMutation = useMutation({
    mutationFn: ({ needId, data }: { needId: string; data: CreateGearClaimRequest }) => 
      gearNeedsApi.claimGearNeed(needId, data),
    onSuccess: async (_, { needId }) => {
      const updatedNeed = await gearNeedsApi.getGearNeedById(needId)
      queryClient.setQueryData<GearNeed[]>(['gearNeeds', tripId], (old = []) => 
        old.map(need => need.id === needId ? updatedNeed : need)
      )
    }
  })

  // Update gear claim mutation
  const updateClaimMutation = useMutation({
    mutationFn: ({ needId, data }: { needId: string; data: UpdateGearClaimRequest }) => 
      gearNeedsApi.updateGearClaim(needId, data),
    onSuccess: async (_, { needId }) => {
      const updatedNeed = await gearNeedsApi.getGearNeedById(needId)
      queryClient.setQueryData<GearNeed[]>(['gearNeeds', tripId], (old = []) => 
        old.map(need => need.id === needId ? updatedNeed : need)
      )
    }
  })

  // Unclaim gear need mutation
  const unclaimMutation = useMutation({
    mutationFn: (needId: string) => gearNeedsApi.unclaimGearNeed(needId),
    onSuccess: async (_, needId) => {
      const updatedNeed = await gearNeedsApi.getGearNeedById(needId)
      queryClient.setQueryData<GearNeed[]>(['gearNeeds', tripId], (old = []) => 
        old.map(need => need.id === needId ? updatedNeed : need)
      )
    }
  })

  // Wrapper functions for mutations
  const createGearNeed = useCallback(async (data: CreateGearNeedRequest): Promise<GearNeed> => {
    if (!tripId) throw new Error('Trip ID is required')
    return await createMutation.mutateAsync(data)
  }, [tripId, createMutation])

  const updateGearNeed = useCallback(async (needId: string, data: UpdateGearNeedRequest): Promise<GearNeed> => {
    return await updateMutation.mutateAsync({ needId, data })
  }, [updateMutation])

  const deleteGearNeed = useCallback(async (needId: string): Promise<void> => {
    await deleteMutation.mutateAsync(needId)
  }, [deleteMutation])

  const claimGearNeed = useCallback(async (needId: string, data: CreateGearClaimRequest): Promise<void> => {
    if (!user) throw new Error('User not authenticated')
    await claimMutation.mutateAsync({ needId, data })
  }, [user, claimMutation])

  const updateGearClaim = useCallback(async (needId: string, data: UpdateGearClaimRequest): Promise<void> => {
    if (!user) throw new Error('User not authenticated')
    await updateClaimMutation.mutateAsync({ needId, data })
  }, [user, updateClaimMutation])

  const unclaimGearNeed = useCallback(async (needId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated')
    await unclaimMutation.mutateAsync(needId)
  }, [user, unclaimMutation])

  const refreshGearNeeds = useCallback(async (): Promise<void> => {
    // Only refetch if data is stale (prevents duplicate refetches after our own mutations)
    // React Query intelligently skips refetch if we just updated the cache
    await queryClient.refetchQueries({ 
      queryKey: ['gearNeeds', tripId],
      type: 'active'
    })
    await queryClient.refetchQueries({ 
      queryKey: ['trip', tripId],
      type: 'active'
    })
  }, [queryClient, tripId])

  return {
    gearNeeds,
    isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isClaiming: claimMutation.isPending || updateClaimMutation.isPending || unclaimMutation.isPending,
    error: (error as Error)?.message || null,
    createGearNeed,
    updateGearNeed,
    deleteGearNeed,
    claimGearNeed,
    updateGearClaim,
    unclaimGearNeed,
    refreshGearNeeds
  }
}
